import type { ReactNode } from 'react'
import { ArrowDown, Shield, AlertTriangle } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { ChainIcon } from '@/components/ChainIcon'
import { TokenIcon } from '@/components/icons/TokenIcons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_HEADING, FONT_BODY, BRAND_GRADIENT } from '@/config/theme'
import { CHAIN_REGISTRY } from '@/config/chains'
import { shortAddr } from '@/utils/format'
import { isCrossChainFeeDisplayEnabled, isWalletTypeLabelEnabled, isDepositAddressNoticeEnabled } from '@/config/flags'
import { isRenderableWalletLabel } from '@/utils/walletLabel'
import { computeDexDisplayTotals, resolveDexFeeDisplay, trimAmt, usdRendersAsZero, type DexTotalInput } from '@/components/FeeSection/dexTotal'
import type { Chain } from '@/types'

interface ReviewTransferModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  amount: string
  estimatedReceived: string
  sourceToken: string
  destToken: string
  sourceChain: Chain | null
  destinationChain: Chain | null
  totalFee: string
  feePercentage: string
  estimatedTime: string
  /**
   * FUND-CRITICAL: must be the SAME derived string that gets signed as the mint recipient
   * (`deriveDestinationRecipient`), never a looser `a || b || ''` fallback. If this can differ from
   * the signed value, the warning branch below describes an address the user is not actually sending
   * to — which is worse than the unconditional "(your wallet)" it replaces. See #143 / findings S7.
   * Empty string means "no recipient resolved yet" and is rendered as such, never as an address.
   */
  recipientAddress: string
  /**
   * #143 (R1). Computed by BridgePage from the IDENTICAL derived string passed as `recipientAddress`
   * (case-sensitive base58 for Solana/Tron, case-insensitive for EVM). The component previously
   * asserted "(your wallet)" unconditionally, which manufactured confidence in exactly the case that
   * loses funds — a pasted Safe, exchange deposit address, or typo-that-passes-regex.
   */
  recipientIsConnectedWallet: boolean
  /**
   * #261 F3. A LABEL — the connected wallet's name and the destination network, e.g.
   * `MetaMask · Robinhood network` — rendered in parentheses BENEATH the address, in place of the
   * generic `(your wallet)`. Built by BridgePage from `utils/walletLabel.ts`.
   *
   * IT MUST NEVER BE AN ADDRESS, and that is enforced here rather than trusted: an address-shaped
   * value is REFUSED and the row falls back to the pre-F3 copy (H3). The prop sits directly under the
   * `recipientAddress` string above, which is the one #143 guarantees equals the signed recipient —
   * so a second, looser recipient string arriving through THIS prop would put two addresses on the
   * row with nothing to tell the user which one receives the funds. That is the exact failure the
   * #143 arc exists to prevent, reintroduced one prop to the left.
   *
   * Absent, empty, or refused ⇒ the row renders `(your wallet)` byte-identically to today, which is
   * also what happens whenever the F3 flag is off (H2). This prop never affects the ADDRESS rendered
   * above it, in any branch.
   */
  recipientWalletLabel?: string | null
  isDexRoute?: boolean
  /**
   * DEX-only: the WHOLE `dexFee` memo (BridgePage.tsx:1619-1686) — the same object the fee card
   * reads. "Total fees" is rendered by running `computeDexDisplayTotals` over THIS, so the modal and
   * the card cannot state different fees (#336 / I3).
   *
   * FUND-DISCLOSURE CRITICAL: never substitute the `totalFee` prop below. That one is
   * `reviewTotalFee` — `input − output` across TWO DIFFERENT TOKENS (BridgePage.tsx:2338-2343) —
   * and feeding it here renders a swap fee ~4000× overstated, in a token it is not denominated in.
   */
  /**
   * ── #261 F8 / C-1: WIDENED, not re-threaded ────────────────────────────────────────────────────
   * BridgePage.tsx:3340 already passes the WHOLE `dexFee` memo, which carries these four fields
   * (BridgePage.tsx:1685-1690). They were simply invisible to TypeScript because `DexTotalInput` is
   * the pure CLAMP contract and must stay that way. So this is a type widening only — no new prop
   * threading, and no field added to `DexTotalInput`.
   *
   * Read the DISCRIMINANT (`youPaySource`) from HERE, never from the separate `youPaySource` prop
   * below: every other number in the fee rows comes out of this object, so branching on a sibling
   * prop lets the branch and the figures come from two places. They are the same object on this tree
   * — the prop is `isDexRoute ? dexFeeEstimate?.youPaySource ?? null : null` — so this is correct and
   * free rather than a live fix, but it is the shape that cannot drift. Aliased once as
   * `payloadYouPaySource` in the body; the destructured prop is read at exactly one site (the
   * "You'll pay" row).
   */
  dexFee?: (DexTotalInput & {
    currency: string
    fixFeeNative?: string
    fixFeeSymbol?: string
    youPaySource?: string
    sourceCurrency?: string
  }) | null
  /** USD → the user's selected fiat, rate applied (hooks/useFiatPrices.ts:17). Absent → plain USD. */
  formatFiatAmount?: (usdcAmount: string | number) => string | null
  /**
   * #261 F8 / C-10. USD value of the receive, for the TO row's fiat subtitle. Non-null only on a
   * NATIVE dest (BridgePage.tsx:1576-1587), where the TO row otherwise renders `0.000794 ETH` and the
   * user has no comparable figure to subtract `Total fees` from — which is every Robinhood route, and
   * the flagship fixture of this change. Without it the corrected total is arithmetic the user cannot
   * perform on screen. Rendered ONLY behind the F8 flag; absent → the TO row is byte-identical.
   */
  estimatedReceivedUsd?: string | null
  /**
   * #261 F5 / C1. USD value of the INPUT (`dex.ts` publishes it as `sourceAmountUsd`). Drives the FROM
   * row's fiat subtitle on a same-chain swap, where the row otherwise reads `0.00041 ETH` and the user
   * has no way to weigh the fee beneath it against what they are spending.
   *
   * There is deliberately NO `?? localAmount` fallback anywhere it is used: null means "we do not know
   * this input's dollar value", and printing the TOKEN amount behind a `$` instead would state
   * `$0.00041` for ~$0.77 — a ~1900× understatement. Absent ⇒ the row is OMITTED. (`SwapCard`'s own
   * `≈` line does exactly that today; it is filed, not fixed here, and is why this prop is spelled out.)
   */
  sourceAmountUsd?: number | null
  /**
   * #261 F5 / C1. Is the DESTINATION token a native coin? Decides whether the TO row's own figure can
   * stand in for its dollar value: a stable dest receive IS ~USD, a native one is not. Passed rather
   * than inferred from `destToken`, so the modal and BridgePage share one answer (BridgePage.tsx:1497).
   */
  isNativeDest?: boolean
  /** DEX-only: true wallet debit (input + opex + deBridge flat fee), source-token units, excl. gas. */
  youPaySource?: string | null
  sourceCurrency?: string
  /** DEX cross-chain ERC-20: the give the router transferFroms = input + opex (source-token units). */
  dexGive?: { input: number; opex: number; give: number } | null
  /**
   * DEV-363 ITEM C. Is this route settled through a generated DEPOSIT ADDRESS — i.e. `near_1click`?
   *
   * A FACT ABOUT THE ROUTE, NOT A DECISION ABOUT THE LINE. BridgePage derives it from the same
   * `selectedRoute?.provider === 'near_1click'` expression it already uses for the 1Click deposit
   * panel (BridgePage.tsx:3945), and passes it unconditionally; the modal re-reads the FLAG itself,
   * exactly as it does for F8 (`feeDisplayOn`) and F3. Keeping the flag decision inside the component
   * means one place answers "does this render", and a call site cannot hold a second, drifting copy.
   *
   * It is passed rather than inferred because the modal is told the route KIND (`isDexRoute`) but
   * never the PROVIDER, and guessing a provider from `sourceChain`/`destToken` would be wrong on every
   * corridor 1Click shares with deBridge.
   *
   * ABSENT or `undefined` ⇒ the line is OMITTED — fail CLOSED. An unknown provider must never be
   * described as a deposit-address route: the line would then be a claim about how the funds move that
   * nothing on the screen supports.
   */
  isDepositAddressRoute?: boolean
  /**
   * #614 — whether this is an ATOMIC same-chain swap (deBridge / Jupiter: one tx, the fiat subtitles
   * under FROM/TO apply). Passed rather than derived from chain equality for the same reason as
   * `isDepositAddressRoute`: a same-chain `near_1click` order is a deposit flow, not an atomic swap.
   * ABSENT ⇒ false (fail closed) — the subtitles are omitted rather than asserted for a route we cannot
   * classify.
   */
  isAtomicSameChain?: boolean
}

const TOKEN_COLORS: Record<string, string> = {
  USDC: '#2775CA', USDT: '#26A17B', ETH: '#627EEA',
  SOL: '#9945FF', BNB: '#F0B90B', MATIC: '#8247E5',
}

function formatDisplay(value: string): string {
  const n = parseFloat(value)
  if (isNaN(n) || n <= 0) return '0.00'
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
}

function formatDeliveryTime(value: string): string {
  // If it already contains letters (e.g. "~2 min"), pass through
  if (/[a-zA-Z]/.test(value)) return value
  const secs = parseInt(value, 10)
  if (isNaN(secs) || secs <= 0) return '~1 min'
  if (secs < 60) return `~${secs} sec`
  const mins = Math.round(secs / 60)
  if (mins <= 1) return '~1 min'
  return `${mins - 1}–${mins + 1} min`
}

export function ReviewTransferModal({
  isOpen,
  onConfirm,
  onCancel,
  amount,
  estimatedReceived,
  sourceToken,
  destToken,
  sourceChain,
  destinationChain,
  totalFee,
  estimatedTime,
  recipientAddress,
  recipientIsConnectedWallet,
  recipientWalletLabel,
  isDexRoute,
  dexFee,
  formatFiatAmount,
  estimatedReceivedUsd,
  sourceAmountUsd,
  isNativeDest,
  youPaySource,
  sourceCurrency,
  dexGive,
  isDepositAddressRoute,
  isAtomicSameChain,
}: ReviewTransferModalProps) {
  const c = useThemeColors()

  /**
   * #261 F8 / C-1 — the payload-internal discriminant, aliased ONCE so the two spellings can never be
   * confused at a use site. Everything that BRANCHES reads this; the destructured `youPaySource`
   * PROP is read at exactly one place, the "You'll pay" row, which is the value it was added for.
   */
  const payloadYouPaySource = dexFee?.youPaySource

  /**
   * #261 F8. The flag gates the whole change; the resolver then picks the arm (ADDENDUM C §7) from
   * the same payload the figures come from. This is one of the TWO display call sites the flag may
   * reach — the third caller of `computeDexDisplayTotals`, `dexFeeUnavailable` (BridgePage.tsx:1788),
   * feeds `canSend` and must keep grading the unflagged all-in in BOTH flag states (C-7 / F-C7).
   */
  const feeDisplayOn = !!isDexRoute && isCrossChainFeeDisplayEnabled()
  const dexDisplay = resolveDexFeeDisplay({
    feesTotalUsd: dexFee?.feesTotalUsd,
    fixFeeUsd: dexFee?.fixFeeUsd,
    // G4-1 — the SAME field the `Bridge & swap cost` row below is gated on (:356). The wedge excludes
    // that fee, so it may only render when that row can; without this the modal stated `≈ $1.01` for
    // a $2.15 cost with the charged $1.14 disclosed nowhere on the screen before the signature.
    fixFeeSymbol: dexFee?.fixFeeSymbol,
    youPaySource: payloadYouPaySource,
    crossChainFeeDisplayEnabled: feeDisplayOn && !!dexFee,
  })

  /**
   * #261 F3 — the connected-wallet label, resolved once, THREE gates deep, all of which must pass
   * before the pre-F3 copy is replaced:
   *
   *   1. the flag                        — H2: flag OFF is byte-identical to today, everywhere
   *   2. a label was actually supplied   — an absent prop reproduces today's copy exactly
   *   3. `isRenderableWalletLabel`       — H3: an ADDRESS-shaped label is refused, not rendered
   *
   * Gate 3 is the one that is not merely defensive. This row is the #143 surface: `recipientAddress`
   * above is guaranteed to be the string that gets signed, and a label that is itself an address
   * would put a second, unguaranteed address on the same row. Refusing here means the guarantee is a
   * property of the ROW rather than of whichever call site happened to build the prop.
   */
  const walletLabelOn = isWalletTypeLabelEnabled()
  const resolvedRecipientLabel =
    walletLabelOn && isRenderableWalletLabel(recipientWalletLabel) ? recipientWalletLabel : null

  const sourceTokenObj = { symbol: sourceToken, color: TOKEN_COLORS[sourceToken] || '#888' }
  const destTokenObj = { symbol: destToken, color: TOKEN_COLORS[destToken] || '#888' }

  // Same helper as the fee card (FeeSection/index.tsx:112): apply the RATE, not just the symbol —
  // `${fiatSymbol}${usd.toFixed(2)}` printed "Rp45.88" for a $45.88 fee where the card printed
  // "Rp745,000", i.e. a mismatch on the screen immediately before signing.
  const fiatFee = (usd: number): string => formatFiatAmount?.(usd) ?? `$${usd.toFixed(2)}`

  /**
   * The clamped display totals for the fee row (#336 / D5).
   *
   * #261 F8 / MED-7 — `computeDexDisplayTotals` takes NO display parameter, so this call is identical
   * in both flag states AND identical to the one the CTA gate makes (BridgePage.tsx:1792). Hoisted
   * out of `renderTotalFees` because the receive subtitle below has to ask the same question the row
   * asks — "is there a stateable fee at all?" — and two copies of that predicate could disagree.
   */
  const dexTotals = dexFee ? computeDexDisplayTotals(dexFee) : null

  /**
   * Does the fee row state a FIGURE (rather than 'Fee unavailable')? #261 F8 / MED-5.
   *
   * The receive subtitle is gated on this: in reverse mode the TO row shows the user's typed target
   * while `estimatedReceivedUsd` comes from the forward estimate, and BridgePage nulls `dexFee` on
   * that mismatch (:1651-1656) — so without this the modal could print a precise `≈ $1.49` directly
   * beneath a row that says the fee cannot be stated. One refusal, one screen: either both figures
   * are trustworthy or neither is shown.
   */
  const dexFeeStated = dexTotals != null && (dexDisplay.wedgeUsd != null || !dexTotals.belowDisplayFloor)

  /**
   * ── #261 F5 / C1 — the per-row `≈ $` on a SAME-CHAIN swap ────────────────────────────────────────
   *
   * The fee row on this screen is denominated in the DEST token (`0.076648 USDG`). Beside a FROM row
   * reading `0.00041 ETH` and a TO row reading `0.689836 USDG`, there is no figure on the screen a
   * user can weigh it against. These two subtitles supply that, each beneath — never in place of —
   * its own token amount.
   *
   * GATED ON SAME-CHAIN DIRECTLY, and NOT on `dexDisplay.branch`. The obvious reading — "same-chain is
   * the `legacy` arm" — is false: `resolveDexFeeDisplay` returns `singleTokenDebit` on
   * `youPaySource != null` (dexTotal.ts:295-302) BEFORE the `legacy` fallback (:317), and same-chain
   * always publishes `youPaySource` (dex.ts:1237-1238). The disjointness from F8's TO-row subtitle
   * holds on other ground: F8's requires `branch === 'stableSplit'`, which same-chain never reaches.
   * This row is deliberately NOT flag-gated — F5 ships `ui=live`.
   */
  const isSameChainSwap = isAtomicSameChain === true
  /**
   * The TO row's dollar figure: the published USD value when there is one (native dest), else the
   * receive itself — which on a STABLE dest already IS dollars. Nothing true to print ⇒ null ⇒ the row
   * is OMITTED, never guessed at.
   *
   * ── H1: BOTH ARMS ARE NARROWED, AND THE NARROWING IS THE WHOLE POINT ────────────────────────────
   * `solana USDC → solana SOL` is SAME-CHAIN with a NATIVE dest, and on that (Jupiter) rail `dex.ts`
   * publishes `estimatedReceived: estimatedTokenAmount` (:780) — a TOKEN amount — which BridgePage hands
   * straight to `estimatedReceivedUsd` (:1583). Taken at face value, either arm would print
   * "≈ $0.01" beneath a receive genuinely worth ≈$2.49: a ~250× understatement on the last screen
   * before a signature. F8 hit this exact case and guarded it with `branch === 'stableSplit'`; that
   * guard cannot be reused here, because SAME-CHAIN is precisely what this row gates on instead.
   * So the two arms carry their own:
   *
   *  · NATIVE-dest arm — the published USD value must DIFFER from the token amount beside it. When
   *    they are the same string, one figure is doing both jobs and it is the token. (That identity is
   *    exactly what `BridgePage.f8CrossChainFee.test.tsx` asserts must never hold on a real USD value.)
   *  · STABLE-dest arm — `isNativeDest === false` EXPLICITLY, not `!isNativeDest`. The prop is
   *    optional, and an absent PROP must not be read as "stable" here.
   *
   *    (B2 — this local check is NOT what fails closed on an unknown TOKEN, and claiming so overstates
   *    it: `isNativeDest` is computed upstream as `TOKEN_PICKER_LIST.find(...)?.isNative ?? false`
   *    (BridgePage.tsx:1497-1500), which already resolves an UNLISTED dest symbol to `false` — i.e.
   *    "stable" — before this component ever sees it. A native token missing from that hardcoded list
   *    arrives here indistinguishable from a genuine stable dest, and `=== false` cannot tell the
   *    difference; it only protects against a genuinely ABSENT prop. Zero blast radius today (every
   *    routed dest token is listed), but armed by a config-only addition. The real fix is filed, not
   *    built here: ADDENDUM C §12 item 5 — publish the dest token's registry `type` on the estimate and
   *    gate on `type === 'stable'` instead of a hardcoded FE list.)
   *
   * The residual cost is a same-chain native-dest row that stays silent when the two figures happen
   * to coincide. That is a missing subtitle, not a wrong one. The root cause — a token amount
   * travelling in a USD-named field — is out of this job's fence (ADDENDUM C §12) and stays filed.
   */
  const toRowUsd: number | null =
    (estimatedReceivedUsd != null && estimatedReceivedUsd !== estimatedReceived)
      ? parseFloat(estimatedReceivedUsd)
    : isNativeDest === false ? parseFloat(estimatedReceived)
    : null

  /**
   * `formatFiatAmount` returns **null for every USD-locale user** (useFiatPrices.ts:27, :50-54 — the
   * rate is null when the currency is 'usd', and 'usd' is the default). Rendering it bare would print
   * a lone `≈` for almost the entire production userbase while a test asserting "the row is present"
   * still passed. The `$x.toFixed(2)` fallback is the USD case, not the forbidden one — what must
   * never be fallen back to is the TOKEN amount.
   */
  const fiatOrUsd = (n: number): string => formatFiatAmount?.(n) ?? `$${n.toFixed(2)}`

  /**
   * The fee-total row (#336 / D5). A function, not an inline ternary, so `dexFee` NARROWS: a
   * `dexTotals != null` guard does not narrow `dexFee` (TS18049) and a `dexFee!` would slip past
   * eslint here, which carries no `no-non-null-assertion` rule.
   *
   * Fails CLOSED: when no truthful positive figure exists the row says so. It must NEVER print a
   * "0" the user reads as "free", and never a figure denominated in a token it is not in.
   */
  const renderTotalFees = (): ReactNode => {
    if (!isDexRoute) return <>{totalFee} {sourceToken}</>
    // Falsy in three distinct situations, all of which read the same here BECAUSE the modal is told
    // the route kind by `isDexRoute` above: the quote is missing or the receive is zero (`undefined`,
    // BridgePage.tsx:1620-1622 — reachable on the Jupiter-503 path, where the user can still submit),
    // or the fee was REFUSED as stale (`null`, :1650). Never derive a fee client-side.
    if (!dexFee || !dexTotals) return 'Fee unavailable'
    // DEV-363 ITEM A — the `dexDisplay.wedgeUsd != null` arm that used to head this chain is GONE.
    // Its only caller is the row below, which is now gated on `wedgeUsd == null`, so the arm was
    // provably unreachable: dead code that looks wired. Every remaining arm is still reachable
    // (`!isDexRoute`, the refusal, `belowDisplayFloor`, and the usd/token pair).
    if (dexTotals.belowDisplayFloor) return 'Fee unavailable'
    return dexTotals.usd != null
      ? `≈ ${fiatFee(dexTotals.usd)}`
      : `${dexTotals.tokenStr} ${dexFee.currency}`
  }

  return (
    <Modal show={isOpen} onClose={onCancel} title="Review transfer">
      <div style={{ padding: '16px 20px 20px' }}>
        {/* FROM / TO card */}
        <div style={{
          background: c.iB, borderRadius: 16, padding: '16px 20px',
          border: `1px solid ${c.dv}`,
        }}>
          {/* FROM */}
          <div style={{
            fontSize: 11, fontWeight: 600, color: c.aC,
            textTransform: 'uppercase', letterSpacing: 1.2,
            fontFamily: FONT_HEADING, marginBottom: 8,
          }}>
            From
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TokenIcon token={sourceTokenObj} size={28} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: c.tx, fontFamily: FONT_HEADING }}>
                {formatDisplay(amount)} {sourceToken}
              </div>
              {/* #261 F5 / C1 — what the input is WORTH, beneath what it IS. Three gates:
                  · `sourceAmountUsd != null` — the backend priced the input. Null means unknown, and
                    the one thing that must never happen here is a token amount behind a `$`.
                  · `dexFeeStated` (F3) — the SAME conjunct the TO row carries, and it is load-bearing
                    HERE for a reason the TO row does not have: this subtitle prints the ESTIMATE's
                    `sourceAmountUsd` beneath the LIVE `bridgeState.amount`. Forward typing is
                    deliberately exempt from the R3/R7 staleness guard (`dexTotal.ts:389`), so inside
                    the ~800 ms debounce+fetch window (300 ms in SwapCard, 500 ms to
                    `debouncedDexAmount`) Review can open showing `0.001 ETH ≈ $7,000` — the PREVIOUS
                    amount's dollars under the current amount's token figure. `dexFeeStated` is the
                    only signal on this component that the quote in hand is one we are willing to state
                    figures from; without it this row had no gate at all. One refusal, one screen.
                  · `usdRendersAsZero` — a sub-half-cent value renders `$0.00`, a false zero, so the
                    row is omitted instead. Fail-CLOSED on NaN by construction of that predicate. */}
              {isSameChainSwap
                && sourceAmountUsd != null
                && dexFeeStated
                && !usdRendersAsZero(sourceAmountUsd) && (
                <div style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  ≈ {fiatOrUsd(sourceAmountUsd)}
                </div>
              )}
              {sourceChain && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <ChainIcon chain={sourceChain} size={14} />
                  <span style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY }}>
                    {CHAIN_REGISTRY[sourceChain].name}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Arrow */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: c.card, border: `1px solid ${c.dv}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ArrowDown size={14} style={{ color: c.mu }} />
            </div>
          </div>

          {/* TO */}
          <div style={{
            fontSize: 11, fontWeight: 600, color: c.aC,
            textTransform: 'uppercase', letterSpacing: 1.2,
            fontFamily: FONT_HEADING, marginBottom: 8,
          }}>
            To
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TokenIcon token={destTokenObj} size={28} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: c.tx, fontFamily: FONT_HEADING }}>
                {formatDisplay(estimatedReceived)} {destToken}
              </div>
              {/* #261 F8 / C-10 — the fiat subtitle that makes the corrected fee row PERFORMABLE. On
                  a native dest this row reads "0.000794 ETH", which cannot be compared with a USD fee
                  figure, so the whole point of the change would be invisible on exactly the routes it
                  targets (every Robinhood corridor). This is a USD figure rendered as a USD figure,
                  beneath — never in place of — the token amount (I-5).

                  THREE GATES, EACH CLOSING A DIFFERENT WAY TO LIE HERE:
                   · `branch === 'stableSplit'` (H1) — `estimatedReceivedUsd` is only a USD value on
                     the deBridge path. A SAME-CHAIN Jupiter swap with a native dest publishes
                     `estimatedReceived: estimatedTokenAmount` (dex.ts:713) — a TOKEN amount — which
                     BridgePage hands over verbatim (:1583), so `solana USDC → solana SOL` rendered
                     "To 0.0128 SOL / ≈ $0.01" against a true ≈$2.49: a ~250× understatement on the
                     signing screen. That path resolves to `singleTokenDebit`/`legacy`, never here.
                     (The ROOT CAUSE — BridgePage treating a token amount as USD — is out of this
                     job's fence and stays filed; this gate is the F8-surface half.)
                   · `dexFeeStated` (MED-5) — never a precise receive figure under 'Fee unavailable'.
                   · `usdRendersAsZero` (L9) — `estimatedReceived` can be the literal string "0.00"
                     (dex.ts:1242) and `formatFiatAmount` returns null at 0, so the `??` fallback
                     below would print "≈ $0.00". Every other USD figure on these two surfaces is
                     floor-gated by this same predicate; this one now is too. Fail-CLOSED on NaN. */}
              {feeDisplayOn
                && estimatedReceivedUsd != null
                && dexDisplay.branch === 'stableSplit'
                && dexFeeStated
                && !usdRendersAsZero(parseFloat(estimatedReceivedUsd)) && (
                <div style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  ≈ {formatFiatAmount?.(estimatedReceivedUsd) ?? `$${parseFloat(estimatedReceivedUsd).toFixed(2)}`}
                </div>
              )}
              {/* #261 F5 / C1 — the SAME-CHAIN counterpart of the row above, and disjoint from it: that
                  one requires `branch === 'stableSplit'`, which same-chain never reaches. Three gates:
                  · `toRowUsd != null` — a published USD value, or a stable dest whose own figure is
                    already dollars. A native dest with neither is omitted, never approximated.
                  · `dexFeeStated` (F8's gate, carried over) — never a precise receive figure sitting
                    under a row that says the fee cannot be stated. One refusal, one screen.
                  · `usdRendersAsZero` — `estimatedReceived` can be the literal '0.00' (dex.ts:1105),
                    which would otherwise print `≈ $0.00`. */}
              {isSameChainSwap
                && toRowUsd != null
                && dexFeeStated
                && !usdRendersAsZero(toRowUsd) && (
                <div style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  ≈ {fiatOrUsd(toRowUsd)}
                </div>
              )}
              {destinationChain && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <ChainIcon chain={destinationChain} size={14} />
                  <span style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY }}>
                    {CHAIN_REGISTRY[destinationChain].name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fee details rows */}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* DEV-363 ITEM A — THE FEE-TOTAL ROW IS WITHHELD ON THE STABLE-SPLIT WEDGE BRANCH, on the
              last screen before a wallet signature. Operator decision (Jira DEV-363, GATE 1
              2026-09-07): the row E8 renamed `Fees from your receive` is REMOVED rather than renamed
              again. Gated on the SAME discriminant the label and figure used to branch on, so the
              removal lands on exactly the one resolver exit that can set it (`dexTotal.ts:361-367`,
              the stableSplit WEDGE sub-branch) — the same gate the fee card carries, so the two
              screens of one journey still cannot disagree about whether this row exists.

              THE ARMS THAT USED TO LIVE HERE ARE GONE, NOT LEFT DEAD: inside this block `wedgeUsd` is
              provably null, so the `'Fees from your receive'` label arm and `renderTotalFees`'s
              matching wedge arm could never render. The label is the literal `Total fees`.

              FLAG-OFF IS UNTOUCHED, STRUCTURALLY. `resolveDexFeeDisplay` returns DISPLAY_OFF on its
              first line when the flag is off (`dexTotal.ts:346`), and DISPLAY_OFF carries
              `wedgeUsd: null` — so this gate is unconditionally TRUE with F8 OFF and this row renders
              exactly as production does today. The other three exits (singleTokenDebit, the C-2/G4-1
              stableSplit fallback, legacy) also carry `wedgeUsd: null` and keep their row.

              TWO SEPARATE CONSEQUENCES, RATIFIED SEPARATELY — do not conflate them. The ticket's
              ACCEPTED TRADE-OFF is the CARD's collapsed-vs-expanded mismatch: `FeeSection/index.tsx:315`
              still prints the all-in `dexTotalUsd` in the collapsed summary, unaffected by this gate,
              so on S2 the expanded panel no longer sums to it. That is what the ticket accepted, at
              ticket time, for the card.

              THE MODAL'S CONSEQUENCE IS DIFFERENT AND WAS RATIFIED SEPARATELY, at the REVIEW-triage
              gate (2026-09-07, operator decision O-2a): `paidSeparately` is scoped to this same wedge
              branch, so the `Bridge & swap cost` row's `— paid separately in <SYM>` annotation below
              now renders on a screen with no fee-total row beside it — the annotation's referent is
              gone, not merely under-summing. On the measured fixture (solana USDC -> robinhood ETH,
              2.5) the modal states no fee total in ANY state; the $1.14 flat fee is the only figure on
              the signing screen, and the $1.01 receive-side wedge (47% of the charge) is on no row.
              Ratified on the record that the $1.01 stays DERIVABLE on screen (2.50 send − $1.49
              receive) — it is the STATED claim that is gone, not the information.

              THE ANNOTATION STAYS ANYWAY, deliberately: it is the only string on this screen stating
              that the flat fee is NOT inside the source-token debit above. Deleting it to remove the
              dangling referent would make that row read as though the flat fee were included — a new
              misstatement, and a worse one than a pointer to a total that isn't on the screen. */}
          {dexDisplay.wedgeUsd == null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: c.mu, fontFamily: FONT_BODY }}>
                Total fees
              </span>
              <span style={{ fontSize: 14, color: c.tx, fontWeight: 500, fontFamily: FONT_BODY }}>
                {renderTotalFees()}
              </span>
            </div>
          )}
          {/* #261 F8 — the rail's flat protocol fee, paid SEPARATELY in the source chain's native
              token (0.015 SOL on Solana) and therefore not inside the source-token debit above. The
              fee card has carried this line since #336 (FeeSection/index.tsx); the modal did not, so
              the last screen before a wallet signature itemized less than the card one screen up.
              This is the modal→card parity half of F8-1b.
              E8 — the USD parenthetical STAYS (C-4/E6's suppression is retired).
              STALE COPY REPAIRED (GATE 4, DEV-363 FIX-B — copy 9 of the F8 contract): this comment
              used to say the row above was renamed `Fees from your receive` and that the two figures
              self-reconciled to the $2.15 all-in. DEV-363 ITEM A retired that row on this branch — on
              the wedge case the fee-total row above is WITHHELD entirely, not renamed (SPACE 1 in the
              committed SURFACE file) — so there is no row left here to reconcile against. The
              conclusion still holds, more simply: with no total rendered on screen at all, a USD
              figure beside this flat fee cannot be "larger than a total" because no total is present
              to compare it to. Annotated "— paid separately in SOL" so the relationship between this
              line and the flat fee is stated, not inferred.
              On a single-token-debit source the line is KEPT and annotated "— included in You'll pay"
              instead (C-8/E7) — the fee IS inside "Total fees" there, and deleting the line would
              unitemise most of it.
              MED-4 — gated on `fixFeeSymbol`, never on a source-token fallback: see the reasoning at
              FeeSection/index.tsx.
              F-1 (S1) — and note the ONE deliberate difference from the card's gate: this row is NEW
              in F8, so the whole condition sits behind `feeDisplayOn` and flag-OFF renders no row at
              all — byte-identical to `2dc934e`, which had none. The card's row PRE-DATES F8, so its
              copy of the symbol precondition carries an explicit flag term to keep flag-OFF a no-op
              there too. Same rule, two ages of row; do not copy the card's extra term to here (it is
              already implied) or drop it there (it is what makes flag-OFF byte-identical).
              G4-1 — that gate is MIRRORED IN THE RESOLVER (`fixFeeSymbol` is a precondition of the
              wedge, dexTotal.ts), so a withheld row always comes with a fee-total row that states the
              all-in CONTAINING this fee. Withholding the row while the wedge rendered excluded the
              charged fee from every figure on this screen — F-2. Keep the two conditions identical. */}
          {feeDisplayOn && dexFee?.fixFeeNative != null && dexFee.fixFeeSymbol != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 14, color: c.mu, fontFamily: FONT_BODY }}>Bridge &amp; swap cost</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, color: c.tx, fontWeight: 500, fontFamily: FONT_BODY }}>
                  {/* The SAME formatter the card uses — `formatDisplay` would render "1.00 ETH" where
                      the card renders "1 ETH" for one identical fee (ADDENDUM C changelog 14). */}
                  {trimAmt(dexFee.fixFeeNative)} {dexFee.fixFeeSymbol}
                  {dexFee.fixFeeUsd != null && !usdRendersAsZero(dexFee.fixFeeUsd) && (
                    <span style={{ color: c.mu, marginLeft: 4 }}>({fiatFee(dexFee.fixFeeUsd)})</span>
                  )}
                </div>
                {dexDisplay.paidSeparately && (
                  <div style={{ fontSize: 12, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                    — paid separately in {dexFee.fixFeeSymbol}
                  </div>
                )}
                {/* #261 F8 / L2 — gated on the DESTRUCTURED prop, i.e. the exact condition that
                    renders the "You'll pay" row below. The annotation used to gate on the PAYLOAD's
                    `youPaySource` (via `includedInYouPay`) while the row it names gates on the prop,
                    so a payload-set/prop-null combination printed "— included in You'll pay" with no
                    You'll pay row anywhere on the screen: a pointer to nothing, on the last screen
                    before a signature. The BRANCH stays payload-driven (C-1); only this annotation,
                    which is a claim about a rendered row, follows that row. */}
                {dexDisplay.includedInYouPay && youPaySource != null && (
                  <div style={{ fontSize: 12, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                    — included in You&apos;ll pay
                  </div>
                )}
              </div>
            </div>
          )}
          {/* You'll pay — the source-token debit the wallet will quote (deBridge flat fee is paid on top
              of the input, so this exceeds "You send"). Excludes network gas, which the wallet adds. */}
          {isDexRoute && youPaySource != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 14, color: c.mu, fontFamily: FONT_BODY }}>You&apos;ll pay</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, color: c.tx, fontWeight: 600, fontFamily: FONT_BODY }}>
                  ≈ {formatDisplay(youPaySource)} {sourceCurrency ?? sourceToken}
                </div>
                <div style={{ fontSize: 12, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  + network gas
                </div>
              </div>
            </div>
          )}
          {/* Cross-chain ERC-20: the router pulls input + opex = give via transferFrom. Disclose the
              give explicitly so the approval amount ("approve 27 for a 10 input") isn't a surprise. */}
          {/* DEV-363 ITEM B — THE WHOLE "You pay" BLOCK IS WITHHELD UNDER F8-ON: label, tooltip,
              value AND subtitle. Operator decision (Jira DEV-363, GATE 1 2026-09-07).

              WHY. Under F8 the row rendered the BARE INPUT (`prependOperatingExpenses=false`,
              debridge-client.ts:159 — the router pulls the input alone, the opex comes out of the
              RECEIVE), which duplicated the FROM block at the top of this modal exactly. A second
              identical figure under a second heading is not disclosure. The `!feeDisplayOn` term is
              the removal's gate; flag OFF the block renders verbatim as production does today —
              legacy value `input + opex fee = give`, legacy tooltip `(input + route fee)` — because
              those are the only arms left. The F8-ON arms of the two ternaries that lived here are
              GONE, not left dead: inside this block `feeDisplayOn` is provably false.

              THE APPROVAL CAVEAT GOES WITH IT, AND THAT COST WAS MEASURED, NOT OVERLOOKED. Both
              channels that carried it — the `title=` tooltip (desktop hover only) and the VISIBLE
              `don't reduce the approval` subtitle — are inside this block, and the strings occur at no
              other site in `apps/web/src`. Accepted because `approveErc20Exact` requests an EXACT
              allowance: a user who reduces it gets a reverted transaction, the retry path re-reads and
              recovers, and the cost is one failed transaction's gas — recoverable, NOT fund loss. */}
          {isDexRoute && dexGive != null && !feeDisplayOn && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <span
                style={{ fontSize: 14, color: c.mu, fontFamily: FONT_BODY }}
                title="Don't reduce this approval in your wallet — the swap pulls the full amount (input + route fee) and reverts if the allowance is lower."
              >
                You pay
              </span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, color: c.tx, fontWeight: 600, fontFamily: FONT_BODY }}>
                  {formatDisplay(String(dexGive.input))} + {formatDisplay(String(dexGive.opex))} fee = {formatDisplay(String(dexGive.give))} {sourceCurrency ?? sourceToken}
                </div>
                <div style={{ fontSize: 12, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  receive ≈ {formatDisplay(estimatedReceived)} {destToken} · don&apos;t reduce the approval
                </div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: c.mu, fontFamily: FONT_BODY }}>Est. delivery</span>
            <span style={{ fontSize: 14, color: c.tx, fontWeight: 500, fontFamily: FONT_BODY }}>
              {formatDeliveryTime(estimatedTime)}
            </span>
          </div>
          {/* DEV-363 ITEM C — THE 1CLICK DEPOSIT-ADDRESS NOTICE. Beta-only behind
              VITE_DEPOSIT_ADDRESS_NOTICE; on a `near_1click` route only. Renders ABOVE the
              Recipient row (repositioned at the REVIEW-triage gate, 2026-09-07): it used to sit
              directly beneath Parse's own fraud warning at the foot of the Recipient block
              ("Not your connected wallet — check every character. Transfers cannot be
              reversed."), which put it in reading order as the explanation FOR that warning
              while it is actually about a different address — the 1Click deposit address, not
              the Recipient. Moving it here removes that misreading.

              WHAT IT IS FOR. 1Click settles by having the user send to a deposit address generated for
              that order, so the wallet sees a destination it has never seen before and flags it as new.
              Today the WALLET is the first thing that tells the user that, which reads as an alarm
              about THIS transfer rather than as a description of how the rail works. Stating the fact
              first, on our own screen, is the whole change.

              THE COPY IS THE OPERATOR'S OWN CLAUSE (Jira DEV-363 comment 10289), not a
              paraphrase: "one line explaining that the destination is a single-use deposit
              address generated for this order, so the wallet will flag it as new." Three
              deliberate choices in the rendered string below:
                · "first sent to" names the INTERMEDIATE HOP, so the line cannot be misread as a
                  claim about the Recipient row above it.
                · "your wallet may show it as a new address" restores the operator's clause and
                  is phrased to hold under either provider behaviour, not asserted as certain.
                · "no prior activity" is gone entirely. NEAR's live docs (fetched 2026-09-07)
                  document "a unique deposit address" per quote and do NOT document single-use,
                  so the stronger, unverified claim has been removed rather than reworded — it
                  was the same assertion `single-use` makes, in different words. Removing it also
                  dissolves the memo-corridor hazard seat B raised at the REVIEW-triage gate: a
                  shared memo-chain deposit address WOULD have prior activity, but "your wallet
                  may show it as a new address" stays true either way. That memo behaviour is a
                  separate, filed defect (GH #587 / DEV-406) and is not this line's to fix.
                · it is PROSPECTIVE and quotes no address: the deposit address does not exist yet
                  at review time — it arrives as `dexBridgeResponse?.depositAddress` after order
                  creation (BridgePage.tsx:3948).

              FOUR GATES, ALL REQUIRED, FAIL-CLOSED ON EACH: the flag, re-read here rather than
              decided at the call site (same shape as F8/F3); the route fact, so an unknown
              provider renders nothing rather than a claim about how its funds move;
              `recipientAddress`, added when this block was repositioned so the line can never
              render while the Recipient row below it still reads "Not set" — this line asserts
              something about where funds are going, and there is nowhere resolved yet; and
              `recipientIsConnectedWallet` (GATE 4, added post-repositioning), which closes a
              cohabitation the reposition itself created: on a PASTED (non-connected-wallet)
              recipient, this line's "your wallet may show it as a new address" now sits one row
              above the Recipient row's own amber warning ("Not your connected wallet — check
              every character. Transfers cannot be reversed."), about a DIFFERENT address. A
              hurried reader could carry this line's predicted-benign "newness" across onto that
              warning. Gating this line to the connected-wallet recipient only removes the
              cohabitation entirely, rather than trying to word around it.
                TRADE-OFF, STATED PLAINLY, NOT FREE: a user who pastes a destination address no
              longer sees this explanation at all, and — because they typed the destination by
              hand rather than having it filled by a connected wallet — they are arguably the
              users most likely to be unsettled by the wallet's own "new address" banner when it
              appears with no forewarning from us. This gate trades that group's forewarning
              against the fraud-warning cohabitation risk above. Accepted by the operator at
              GATE 4.

              PROMINENCE. `fontSize: 13`, matching the trust panel below rather than the smaller
              12px annotations elsewhere on this screen: this is the one line whose whole purpose
              is to speak before the wallet does. */}
          {isDepositAddressNoticeEnabled() && isDepositAddressRoute && recipientAddress && recipientIsConnectedWallet && (
            <div
              data-testid="deposit-address-notice"
              style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, lineHeight: 1.4 }}
            >
              This transfer is first sent to a deposit address created for this order, so your wallet may show it as a new address.
            </div>
          )}
          {/* Recipient (#143 / R1).
              Three distinct states, because collapsing any two of them is what made this row lie:
               - no recipient resolved  → say so; NEVER render a label against a blank address
               - recipient IS the connected wallet → unchanged TRUNCATION; the copy beneath is
                 `(your wallet)` today and, behind the #261 F3 flag, the wallet + network label
               - recipient is anything else → warning + the FULL address. shortAddr hides the middle
                 26 characters, which is precisely where a wrong-but-valid address is indistinguishable
                 from the right one, so truncating here would defeat the warning. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 14, color: c.mu, fontFamily: FONT_BODY, flexShrink: 0 }}>Recipient</span>
            {!recipientAddress ? (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, color: c.mu, fontWeight: 500, fontFamily: FONT_BODY }}>
                  Not set
                </div>
                <div style={{ fontSize: 12, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  Connect your destination wallet or enter a recipient address.
                </div>
              </div>
            ) : recipientIsConnectedWallet ? (
              <div style={{ textAlign: 'right' }}>
                {/* #261 F3 — THE ADDRESS LINE IS UNTOUCHED. Same `shortAddr(recipientAddress)`, same
                    element, same style, in both flag states: F3 changes the LABEL beneath it and
                    nothing else, which is what keeps MP-2 / #143 true by construction rather than by
                    assertion. */}
                <div style={{ fontSize: 14, color: c.tx, fontWeight: 500, fontFamily: FONT_BODY }}>
                  {shortAddr(recipientAddress)}
                </div>
                {/* The label. `(your wallet)` was actively inverted on a Robinhood destination — the
                    connected MetaMask on chain 4663 is NOT the user's Robinhood app balance, which is
                    the #261 live outcome. Naming the wallet AND the network is what lets the user
                    catch that themselves; per the operator's LABEL-ONLY lock there is deliberately NO
                    warning treatment here (that copy belongs to the pasted branch below, where it is
                    true). */}
                <div style={{ fontSize: 12, color: c.mu, fontFamily: FONT_BODY, marginTop: 1 }}>
                  {resolvedRecipientLabel ? `(${resolvedRecipientLabel})` : '(your wallet)'}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'right', minWidth: 0 }}>
                <div
                  data-testid="recipient-full-address"
                  style={{
                    fontSize: 13, color: c.tx, fontWeight: 600,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    wordBreak: 'break-all', lineHeight: 1.35,
                  }}
                >
                  {recipientAddress}
                </div>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 5,
                  marginTop: 4,
                }}>
                  <AlertTriangle size={13} style={{ color: '#E8A33D', flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 12, color: '#E8A33D', fontFamily: FONT_BODY, lineHeight: 1.35 }}>
                    Not your connected wallet — check every character. Transfers cannot be reversed.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust message */}
        <div style={{
          marginTop: 16, padding: '12px 14px',
          background: c.iB, borderRadius: 12,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <Shield size={16} style={{ color: c.mu, flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: c.mu, fontFamily: FONT_BODY, lineHeight: 1.45 }}>
            {isDexRoute
              ? 'Your wallet will ask you to sign to authorize the swap. Parse DeFi never holds or custodies your funds.'
              : 'Your wallet will ask you to sign twice \u2014 once to authorize the USDC transfer, once to confirm the bridge. Parse DeFi never holds or custodies your funds.'}
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px 16px',
              borderRadius: 14, border: `1px solid ${c.dv}`,
              background: 'transparent', color: c.tx,
              fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 2, padding: '12px 16px',
              borderRadius: 14, border: 'none',
              background: BRAND_GRADIENT, color: '#FFFFFF',
              fontSize: 15, fontWeight: 600, fontFamily: FONT_HEADING,
              cursor: 'pointer',
            }}
          >
            Confirm &amp; send
          </button>
        </div>
      </div>
    </Modal>
  )
}
